import React, { useState, useRef } from 'react';
import { Plus } from 'lucide-react';
import UserPropertyFilterModule from './UserPropertyFilterModule';
import UserFilterChip from './UserFilterChip';
import UserWhoFilterChip from './UserWhoFilterChip';

const isUserProperty = (f) => (f.propertySource || f.category) === 'user';
const isWhoProperty = (f) => (f.propertySource || f.category) === 'event' || (f.propertySource || f.category) === 'transaction';

/**
 * Users filter module: WHERE block (above) + WHO block (below).
 * - WHERE: User properties with WHERE/AND/OR
 * - WHO: Events and Transactions with WHO/AND/OR, Did/Did not, numeric operators
 */
const UsersFilterModule = ({
  filters = [],
  onFiltersChange,
  teamId,
}) => {
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState(null);
  const addButtonRef = useRef(null);

  const userFilters = (filters || []).filter(isUserProperty);
  const whoFilters = (filters || []).filter(isWhoProperty);

  const handleAddFilter = () => {
    setEditingFilter(null);
    setIsAddPopupOpen(true);
  };

  const handlePropertyAdd = (newFilter) => {
    const isWho = isWhoProperty(newFilter);
    const targetList = isWho ? whoFilters : userFilters;
    const firstConnector = isWho ? 'WHO' : 'WHERE';

    const connector = targetList.length === 0
      ? firstConnector
      : (filters.some((f) => f.id === newFilter.id) ? (filters.find((f) => f.id === newFilter.id)?.connector || 'AND') : 'AND');

    const withConnector = { ...newFilter, connector };
    if (filters.some((f) => f.id === newFilter.id)) {
      onFiltersChange(filters.map((f) => (f.id === newFilter.id ? withConnector : f)));
    } else {
      onFiltersChange([...filters, withConnector]);
    }
    setIsAddPopupOpen(false);
    setEditingFilter(null);
  };

  const handleFilterUpdate = (filterId, updates) => {
    onFiltersChange(filters.map((f) => (f.id === filterId ? { ...f, ...updates } : f)));
  };

  const handleFilterRemove = (filterId) => {
    const remaining = filters.filter((f) => f.id !== filterId);
    const userRemaining = remaining.filter(isUserProperty);
    const whoRemaining = remaining.filter(isWhoProperty);
    const withConnectors = remaining.map((f) => {
      const isWho = isWhoProperty(f);
      const list = isWho ? whoRemaining : userRemaining;
      const idx = list.findIndex((x) => x.id === f.id);
      const connector = idx === 0 ? (isWho ? 'WHO' : 'WHERE') : (f.connector === 'OR' ? 'OR' : 'AND');
      return { ...f, connector };
    });
    onFiltersChange(withConnectors);
    if (editingFilter?.id === filterId) {
      setEditingFilter(null);
      setIsAddPopupOpen(false);
    }
  };

  const handleEditFilter = (filter) => {
    setEditingFilter(filter);
    setIsAddPopupOpen(true);
  };

  const hasFilters = filters.length > 0;

  return (
    <div className="bg-transparent border border-dotted border-gray-300 rounded-lg overflow-hidden" style={{ fontFamily: 'Raleway, sans-serif' }}>
      <div className="bg-transparent overflow-hidden">
        <div className="pl-4 pr-6 pt-2 pb-3 flex items-center justify-between border-b border-dotted border-gray-300">
          <h3 className="text-xs font-bold uppercase tracking-wide text-[#0f172a]">
            FILTERS
          </h3>
        </div>
        <div className="p-6">
          {/* WHERE block (above) - User properties */}
          {userFilters.length > 0 && (
            <div className="space-y-2 mb-4">
              {userFilters.map((filter, index) => (
                <UserFilterChip
                  key={filter.id}
                  filter={{
                    ...filter,
                    propertySource: 'user',
                    property: filter.property || filter.field,
                    propertyLabel: filter.propertyLabel,
                    propertyType: filter.propertyType,
                  }}
                  isFirst={index === 0}
                  teamId={teamId}
                  onEdit={() => handleEditFilter(filter)}
                  onUpdate={(updates) => handleFilterUpdate(filter.id, updates)}
                  onRemove={() => handleFilterRemove(filter.id)}
                />
              ))}
            </div>
          )}

          {/* WHO block (below) - Events and Transactions */}
          {whoFilters.length > 0 && (
            <div className="space-y-2 mb-4">
              {whoFilters.map((filter, index) => (
                <UserWhoFilterChip
                  key={filter.id}
                  filter={{
                    ...filter,
                    propertySource: filter.propertySource || filter.category || 'event',
                    property: filter.property || filter.field,
                    propertyLabel: filter.propertyLabel,
                    propertyType: filter.propertyType,
                    didOperator: filter.didOperator || 'did',
                  }}
                  isFirst={index === 0}
                  teamId={teamId}
                  onEdit={() => handleEditFilter(filter)}
                  onUpdate={(updates) => handleFilterUpdate(filter.id, updates)}
                  onRemove={() => handleFilterRemove(filter.id)}
                />
              ))}
            </div>
          )}

          <div>
            <button
              ref={addButtonRef}
              type="button"
              onClick={handleAddFilter}
              className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#0f172a] transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Add</span>
            </button>
          </div>
        </div>
      </div>

      {hasFilters && (
        <div className="px-6 pb-2">
          <button
            type="button"
            onClick={() => onFiltersChange([])}
            className="text-sm text-[#475569] hover:text-[#0f172a]"
          >
            Clear all
          </button>
        </div>
      )}

      {(isAddPopupOpen || editingFilter) && (
        <UserPropertyFilterModule
          isOpen={isAddPopupOpen || !!editingFilter}
          onClose={() => {
            setIsAddPopupOpen(false);
            setEditingFilter(null);
          }}
          triggerRef={addButtonRef}
          teamId={teamId}
          filters={filters}
          existingFilter={editingFilter}
          onAdd={handlePropertyAdd}
        />
      )}
    </div>
  );
};

export default UsersFilterModule;
